import { httpsCallable } from 'firebase/functions';
import {
  CollectionItemMoveResult,
  MoveCollectionItemsRequest,
  parseMoveCollectionItemsRequest,
} from '@qkindex/contracts';
import { functions } from '../../firebase';

const move = httpsCallable<MoveCollectionItemsRequest, unknown>(
  functions,
  'moveCollectionItems',
);
const list = httpsCallable<
  { sourceCollectionId: string; cursor?: string },
  unknown
>(functions, 'listCollectionItemMoves');

export function parseItemMoveResult(value: unknown): CollectionItemMoveResult {
  if (!value || typeof value !== 'object')
    throw new Error('Invalid move result.');
  const fields = value as CollectionItemMoveResult;
  const request = parseMoveCollectionItemsRequest({
    sourceCollectionId: fields.sourceCollectionId,
    destinationCollectionId: fields.destinationCollectionId,
    destinationLocationId: fields.destinationLocationId,
    operationId: fields.operationId,
    itemIds: fields.itemIds,
    ...(fields.destinationTagIds === undefined
      ? {}
      : { destinationTagIds: fields.destinationTagIds }),
  });
  const result = value as Partial<CollectionItemMoveResult>;
  if (
    !['processing', 'complete', 'partial'].includes(result.state || '') ||
    !Array.isArray(result.entries) ||
    result.entries.length !== request.itemIds.length
  )
    throw new Error('Invalid move result.');
  const ids = new Set<string>();
  for (const entry of result.entries) {
    if (
      !entry ||
      !request.itemIds.includes(entry.sourceItemId) ||
      ids.has(entry.sourceItemId) ||
      typeof entry.destinationItemId !== 'string' ||
      !entry.destinationItemId ||
      !['pending', 'moved', 'failed'].includes(entry.state)
    )
      throw new Error('Invalid move result.');
    ids.add(entry.sourceItemId);
  }
  return { ...request, state: result.state!, entries: result.entries };
}

export async function moveItems(
  request: MoveCollectionItemsRequest,
): Promise<CollectionItemMoveResult> {
  const payload = parseMoveCollectionItemsRequest({
    sourceCollectionId: request.sourceCollectionId,
    destinationCollectionId: request.destinationCollectionId,
    destinationLocationId: request.destinationLocationId,
    operationId: request.operationId,
    itemIds: request.itemIds,
    ...(request.destinationTagIds === undefined
      ? {}
      : { destinationTagIds: request.destinationTagIds }),
  });
  const result = parseItemMoveResult((await move(payload)).data);
  if (
    result.operationId !== request.operationId ||
    result.sourceCollectionId !== request.sourceCollectionId ||
    result.destinationCollectionId !== request.destinationCollectionId ||
    result.destinationLocationId !== request.destinationLocationId ||
    [...result.itemIds].sort().join('\0') !==
      [...request.itemIds].sort().join('\0')
  )
    throw new Error('Move response changed.');
  return result;
}

export async function listItemMoves(
  sourceCollectionId: string,
  cursor?: string,
): Promise<{ moves: CollectionItemMoveResult[]; nextCursor?: string }> {
  const value = (
    await list({ sourceCollectionId, ...(cursor ? { cursor } : {}) })
  ).data as { moves?: unknown[]; nextCursor?: unknown };
  if (
    !value ||
    !Array.isArray(value.moves) ||
    (value.nextCursor !== undefined && typeof value.nextCursor !== 'string')
  )
    throw new Error('Invalid move history.');
  const moves = value.moves.map(parseItemMoveResult);
  if (moves.some((entry) => entry.sourceCollectionId !== sourceCollectionId))
    throw new Error('Invalid move history.');
  return {
    moves,
    ...(typeof value.nextCursor === 'string'
      ? { nextCursor: value.nextCursor }
      : {}),
  };
}
